import React, { RefObject, useEffect, useMemo, useState } from 'react';
import './SenderSelector.pcss';
import Autosuggest, { InputProps } from 'react-autosuggest';
import AttributeModel from '@mm/state/model/AttributeModel';
import PageModel from '@mm/state/model/PageModel';
import './DialogueContainigTextInput.pcss';
import { useSelector } from 'react-redux';
import { selectPages } from '@mm/state/selectors/pageSelectors';

type Props = {
  inputProps: InputProps<PageModel>;
  textareaRef: RefObject<HTMLTextAreaElement> | null;
  setRef: (ref: RefObject<HTMLTextAreaElement> | null) => void;
  setMessage: (text: string) => void;
  onDialogueSelected: (
    e?: any,
    dialogueParams?: { id: number; name: string },
  ) => void;
  launchDialogue: (dialogueParams: { id: number; name: string }) => void;
  selectedDialogue: PageModel | null;
  setSelectedDialogue: (dialogue: PageModel | null) => void;
};

export const DialogueContainingTextInput = (props: Props) => {
  const {
    inputProps,
    setRef,
    textareaRef,
    setMessage,
    onDialogueSelected,
    launchDialogue,
    selectedDialogue,
    setSelectedDialogue,
  } = props;
  const { value } = inputProps;
  const dialogues = useSelector(selectPages);

  const cursorPosition =
    textareaRef?.current && textareaRef.current.selectionStart;

  const [dialogueNameFilter, setDialogueNameFilter] = useState('');
  const [braceActivated, setBraceActivated] = useState(false);

  const showSuggestions = useMemo(() => {
    if (!braceActivated || !cursorPosition || value.length < 2) {
      return false;
    }
    const searchText = value.substr(0, cursorPosition);
    const parts = searchText.split('{{');
    if (parts.length <= 1) {
      return false;
    }
    const foundDialogueNameFilter = parts.pop() as string;

    if (foundDialogueNameFilter.indexOf('}') >= 0) {
      return false;
    }

    if (foundDialogueNameFilter !== dialogueNameFilter) {
      setDialogueNameFilter(foundDialogueNameFilter);
    }
    return true;
  }, [braceActivated, cursorPosition, value]);

  const onKeyDown =
    (inputProps: Autosuggest.InputProps<AttributeModel>) =>
    async (e: React.KeyboardEvent) => {
      const blockEventIfSuggestionsShown = () => {
        if (showSuggestions) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      switch (e.key) {
        case '{':
          blockEventIfSuggestionsShown();
          setBraceActivated(true);
          setDialogueNameFilter('');
          break;
        case 'Escape':
          setBraceActivated(false);
          setDialogueNameFilter('');
          return;
        case 'Tab':
          onSuggestionSelected(suggestions[0]);
          blockEventIfSuggestionsShown();
          break;
        case 'Enter':
          e.stopPropagation();
          if (selectedDialogue) {
            e.preventDefault();
            setMessage('');
            await launchDialogue({
              id: selectedDialogue.id,
              name: selectedDialogue.name,
            });
            setSelectedDialogue(null);
          } else if (!showSuggestions && !e.shiftKey) {
            e.preventDefault();
            onDialogueSelected();
          }
          break;
        case 'Home':
        case 'End':
        case 'PageUp':
        case 'PageDown':
        case 'ArrowLeft':
        case 'ArrowRight':
          blockEventIfSuggestionsShown();
          break;
        default:
          break;
      }
      inputProps.onKeyDown && inputProps.onKeyDown(e);
    };

  useEffect(() => {
    const matchValue = value.match(/{{(.*?)}}/g);
    if (!matchValue) {
      setSelectedDialogue(null);
    }
  }, [value]);

  const onSuggestionSelected = (suggestion: PageModel) => {
    const pos = cursorPosition || value.length;
    const text = `${suggestion.name}}}`;
    setSelectedDialogue(suggestion);
    const newValue =
      value.slice(0, pos - dialogueNameFilter.length) + text + value.slice(pos);
    setMessage(newValue);

    setBraceActivated(false);
    setDialogueNameFilter('');

    // Move cursor to position right after inserted attribute.
    setTimeout(() => {
      const current = textareaRef?.current;
      if (current) {
        current.focus();
        current.selectionStart = pos + text.length;
        current.selectionEnd = pos + text.length;
        current.scrollLeft =
          ((current.scrollWidth - current.clientWidth) * (pos + text.length)) /
          value.length;
      }
    }, 10);
  };

  const renderInputComponent = (
    inputProps: Autosuggest.InputProps<AttributeModel>,
  ) => {
    const setBothRefs = ref => {
      inputProps.ref(ref);
      setRef(ref);
    };

    return (
      <textarea
        {...inputProps}
        onChange={inputProps.onChange as any}
        ref={setBothRefs}
        onKeyDown={onKeyDown(inputProps)}
      />
    );
  };

  const renderSuggestion = (
    suggestion: AttributeModel,
    isHighlighted: boolean,
  ) => (
    <div
      style={{
        background: isHighlighted ? '#0c7cd5' : 'white',
        color: isHighlighted ? 'white' : 'inherit',
      }}
    >
      {`{{${suggestion.name}}}`}
    </div>
  );
  const suggestions = useMemo(
    () =>
      dialogues.filter(a =>
        a.name.toLowerCase().startsWith(dialogueNameFilter.toLowerCase()),
      ),
    [dialogues, dialogueNameFilter],
  );

  return (
    <Autosuggest
      inputProps={inputProps}
      onSuggestionsFetchRequested={() => {}}
      onSuggestionsClearRequested={() => {}}
      getSuggestionValue={s => undefined as any}
      suggestions={showSuggestions ? suggestions : []}
      alwaysRenderSuggestions={showSuggestions}
      onSuggestionSelected={(e, { suggestion }) =>
        onSuggestionSelected(suggestion)
      }
      renderInputComponent={renderInputComponent}
      renderSuggestion={(suggestion, { isHighlighted }) =>
        renderSuggestion(suggestion, isHighlighted)
      }
    />
  );
};
